import type { Aspect, Design, Widget } from './types';
import { ASPECTS } from './types';
import { posterRatio, sizeFor } from './widgets';

/** Every widget sits on whole cells. These helpers are the only way positions change, so nothing is ever freeform. */

export interface Rect { x: number; y: number; w: number; h: number }
/** Limits for reading older designs; new designs always use the aspect's fixed grid. */
export const MIN_COLS = 2, MAX_COLS = 48, MIN_ROWS = 2, MAX_ROWS = 48;

export const overlaps = (a: Rect, b: Rect) => a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;

export function minSize(w: Pick<Widget, 'type'>, cols: number, rows: number) {
  const s = sizeFor(w.type, cols, rows);
  return { w: s.minW, h: s.minH };
}

/** Keeps a rectangle inside the grid and at least its minimum size. */
export function clampRect(r: Rect, cols: number, rows: number, min = { w: 1, h: 1 }): Rect {
  const w = Math.max(Math.min(min.w, cols), Math.min(cols, Math.round(r.w)));
  const h = Math.max(Math.min(min.h, rows), Math.min(rows, Math.round(r.h)));
  const x = Math.max(0, Math.min(cols - w, Math.round(r.x)));
  const y = Math.max(0, Math.min(rows - h, Math.round(r.y)));
  return { x, y, w, h };
}

export function isFree(widgets: readonly Widget[], r: Rect, ignoreId?: string): boolean {
  return !widgets.some((w) => w.id !== ignoreId && !w.hidden && overlaps(w, r));
}

/** First empty area of w × h cells, scanning left-to-right, top-to-bottom; null if the grid is full. */
export function findSpot(widgets: readonly Widget[], w: number, h: number, cols: number, rows: number): Rect | null {
  for (let y = 0; y + h <= rows; y++) for (let x = 0; x + w <= cols; x++) {
    const r = { x, y, w, h };
    if (isFree(widgets, r)) return r;
  }
  return null;
}

/** Best place for a new widget: its default size if there's room, otherwise the largest free size down to its minimum. */
export function placeNew(widgets: readonly Widget[], type: Widget['type'], cols: number, rows: number, at?: { x: number; y: number }): Rect {
  const s = sizeFor(type, cols, rows);
  // Makruh Times goes along the bottom of the screen (it's hidden except during makruh times, so it may cover others).
  if (type === 'makruh') return { x: 0, y: rows - s.h, w: s.w, h: s.h };
  if (at) {
    const r = clampRect({ x: at.x, y: at.y, w: s.w, h: s.h }, cols, rows, { w: s.minW, h: s.minH });
    return r;
  }
  for (let h = s.h; h >= s.minH; h--) for (let w = s.w; w >= s.minW; w--) {
    const r = findSpot(widgets, w, h, cols, rows);
    if (r) return r;
  }
  return { x: 0, y: 0, w: s.w, h: s.h }; // grid full: place on top; the Layers panel shows it in front
}

export type Handle = 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw';

/** New rectangle after dragging `handle` by (dx, dy) cells. The opposite edge stays put. */
export function resizeRect(start: Rect, handle: Handle, dx: number, dy: number, cols: number, rows: number, min: { w: number; h: number }): Rect {
  let { x, y, w, h } = start;
  const right = start.x + start.w, bottom = start.y + start.h;
  if (handle.includes('e')) w = Math.max(min.w, Math.min(cols - x, start.w + dx));
  if (handle.includes('s')) h = Math.max(min.h, Math.min(rows - y, start.h + dy));
  if (handle.includes('w')) { x = Math.max(0, Math.min(right - min.w, start.x + dx)); w = right - x; }
  if (handle.includes('n')) { y = Math.max(0, Math.min(bottom - min.h, start.y + dy)); h = bottom - y; }
  return { x, y, w, h };
}

/** Maps a layout onto a different grid, keeping each widget's proportional position. */
export function regrid(widgets: readonly Widget[], from: { cols: number; rows: number }, to: { cols: number; rows: number }): Widget[] {
  const fx = to.cols / from.cols, fy = to.rows / from.rows;
  return widgets.map((wd) => {
    const x0 = Math.round(wd.x * fx), y0 = Math.round(wd.y * fy);
    const x1 = Math.round((wd.x + wd.w) * fx), y1 = Math.round((wd.y + wd.h) * fy);
    const min = minSize(wd, to.cols, to.rows);
    const r = clampRect({ x: x0, y: y0, w: Math.max(1, x1 - x0), h: Math.max(1, y1 - y0) }, to.cols, to.rows, min);
    return { ...wd, ...r };
  });
}

/** Puts a design on its aspect's fixed grid, keeping each widget in proportion (for designs saved before grids were fixed). */
export function toFixedGrid(design: Design): Design {
  const a = ASPECTS[design.aspect];
  if (design.cols === a.cols && design.rows === a.rows) return design;
  return reshapeFixed({ ...design, cols: a.cols, rows: a.rows, widgets: regrid(design.widgets, design, a) });
}

export function setAspect(design: Design, aspect: Aspect): Design {
  if (aspect === design.aspect) return design;
  const a = ASPECTS[aspect];
  return reshapeFixed({ ...design, aspect, cols: a.cols, rows: a.rows, widgets: regrid(design.widgets, design, a) });
}

/** After a layout is stretched onto another grid, fixed-shape widgets (posters) go back to their real shape. */
export function reshapeFixed(design: Design): Design {
  if (!design.widgets.some((w) => lockedRatio(w))) return design;
  const m = gridMetrics(design);
  return { ...design, widgets: design.widgets.map((w) => { const r = lockedRatio(w); return r ? { ...w, ...containShape(w, r, m) } : w; }) };
}

export const MARGINS = { small: 0.025, medium: 0.04, large: 0.06 } as const;
export const MIN_MARGIN = 0.02, MAX_MARGIN = 0.1, DEFAULT_MARGIN = MARGINS.medium;

/** Spacing between widgets. The step from one to the next is clearly visible on a TV without looking heavy. */
export const GAPS = { none: 0, small: 0.06, medium: 0.12, large: 0.2 } as const;
export const DEFAULT_GAP = GAPS.medium;

/** Pixel geometry of the grid at the display's native resolution. */
export function gridMetrics(design: Pick<Design, 'aspect' | 'cols' | 'rows' | 'gap'> & { margin?: number }) {
  const { width, height } = ASPECTS[design.aspect];
  const short = Math.min(width, height);
  const margin = Math.round(Math.max(MIN_MARGIN, Math.min(MAX_MARGIN, design.margin ?? DEFAULT_MARGIN)) * short);
  // Spacing scales with the screen, not the cells, so it looks the same in every orientation.
  // Medium is about 21 px on a 1080p screen, large about 35 px.
  const gap = Math.round(Math.max(0, Math.min(0.5, design.gap)) * short * 0.16);
  const cellW = (width - 2 * margin - gap * (design.cols - 1)) / design.cols;
  const cellH = (height - 2 * margin - gap * (design.rows - 1)) / design.rows;
  /** Corner radius of every widget. */
  const radius = Math.round(short * 0.016);
  return { width, height, gap, margin, cellW, cellH, radius };
}

export function rectToPx(r: Rect, m: ReturnType<typeof gridMetrics>) {
  return {
    left: m.margin + r.x * (m.cellW + m.gap),
    top: m.margin + r.y * (m.cellH + m.gap),
    width: r.w * m.cellW + (r.w - 1) * m.gap,
    height: r.h * m.cellH + (r.h - 1) * m.gap,
  };
}

/** Cell under a point given in display pixels (for dropping and dragging). */
export function pxToCell(px: number, py: number, m: ReturnType<typeof gridMetrics>, cols: number, rows: number) {
  return {
    x: Math.max(0, Math.min(cols - 1, Math.floor((px - m.margin + m.gap / 2) / (m.cellW + m.gap)))),
    y: Math.max(0, Math.min(rows - 1, Math.floor((py - m.margin + m.gap / 2) / (m.cellH + m.gap)))),
  };
}

export const cellsLabel = (w: Rect) => `${w.w} × ${w.h}`;

/* ---------- fixed shapes (posters) ---------- */

/** Width ÷ height a widget must keep, or null if it can be any shape. */
export function lockedRatio(w: Pick<Widget, 'type' | 'props'>): number | null {
  return w.type === 'posters' ? posterRatio((w.props as { shape?: unknown }).shape ?? 'a-portrait') : null;
}

const spanPx = (n: number, cell: number, gap: number) => n * cell + (n - 1) * gap;
/** Number of cells whose span is closest to `px` (at least 1, at most `max`). */
function cellsFor(px: number, cell: number, gap: number, max: number) {
  let best = 1, err = Infinity;
  for (let n = 1; n <= max; n++) { const e = Math.abs(spanPx(n, cell, gap) - px); if (e < err) { err = e; best = n; } }
  return best;
}

/**
 * Keeps a fixed-shape widget at its shape on the grid. `drive` is the side the editor changed; the other side follows,
 * picking the whole number of cells that matches the shape most closely. If that doesn't fit, both sides shrink.
 * `keep` says which edges stay put ('end' keeps the right or bottom edge, for resizing from the left or top).
 */
export function fitShape(r: Rect, ratio: number, m: ReturnType<typeof gridMetrics>, cols: number, rows: number,
  drive: 'w' | 'h', keep: { x: 'start' | 'end'; y: 'start' | 'end' } = { x: 'start', y: 'start' }, min = { w: 1, h: 1 }): Rect {
  let { w, h } = r;
  for (let guard = 0; guard < 64; guard++) {
    if (drive === 'h') w = cellsFor(spanPx(h, m.cellH, m.gap) * ratio, m.cellW, m.gap, 999);
    else h = cellsFor(spanPx(w, m.cellW, m.gap) / ratio, m.cellH, m.gap, 999);
    if (w <= cols && h <= rows) break;
    if (drive === 'h') h = Math.max(min.h, h - 1); else w = Math.max(min.w, w - 1);
    if ((drive === 'h' && h === min.h) || (drive === 'w' && w === min.w)) { w = Math.min(w, cols); h = Math.min(h, rows); break; }
  }
  w = Math.max(Math.min(min.w, cols), Math.min(cols, w));
  h = Math.max(Math.min(min.h, rows), Math.min(rows, h));
  const x = keep.x === 'end' ? r.x + r.w - w : r.x;
  const y = keep.y === 'end' ? r.y + r.h - h : r.y;
  return { x: Math.max(0, Math.min(cols - w, x)), y: Math.max(0, Math.min(rows - h, y)), w, h };
}

/** The largest rectangle of the widget's shape that fits inside `box`, centred in it. */
export function containShape(box: Rect, ratio: number, m: ReturnType<typeof gridMetrics>): Rect {
  let best: Rect = box, bestErr = Infinity;
  for (let h = box.h; h >= 1; h--) for (let w = box.w; w >= 1; w--) {
    const err = Math.abs(Math.log(spanPx(w, m.cellW, m.gap) / spanPx(h, m.cellH, m.gap) / ratio));
    // Prefer bigger rectangles; accept a slightly worse shape only for a much bigger area.
    const score = err * 4 - Math.log((w * h) / (box.w * box.h));
    if (err < 0.06 && score < bestErr) { bestErr = score; best = { x: box.x + Math.floor((box.w - w) / 2), y: box.y + Math.floor((box.h - h) / 2), w, h }; }
  }
  return best;
}

/** Applies a widget's fixed shape after any change to its size (no-op for widgets of any shape). */
export function keepShape(w: Widget, r: Rect, design: Design, drive: 'w' | 'h', keep?: { x: 'start' | 'end'; y: 'start' | 'end' }): Rect {
  const ratio = lockedRatio(w);
  if (!ratio) return r;
  return fitShape(r, ratio, gridMetrics(design), design.cols, design.rows, drive, keep, minSize(w, design.cols, design.rows));
}

/** Largest corner radius a widget can have (px at native size), so a corner curve never turns it into a circle. */
export const MAX_RADIUS = 64;
/** A widget's corner radius in display px: its own, or the screen's standard, never more than ~a fifth of its shorter side. */
export function cornerRadius(w: Pick<Widget, 'x' | 'y' | 'w' | 'h' | 'radius'>, m: ReturnType<typeof gridMetrics>): number {
  const px = rectToPx(w, m);
  const cap = Math.min(MAX_RADIUS, Math.min(px.width, px.height) * 0.22);
  return Math.round(Math.min(w.radius ?? m.radius, cap));
}
